/**
 * Pure formatters for trigger definitions — human-readable summaries like
 * "Every Monday 09:00" or "Every 15 min · query 'Low stock'".
 *
 * Returns `{ key, params }` descriptors (resolved with i18n at render time
 * under `ai.triggersPanel.summary.*`) so the logic stays unit-testable
 * without an i18n instance.
 */

import type {
  EventDefinition,
  PollDefinition,
  ScheduleDefinition,
  ScheduleSpec,
  Trigger,
} from "./types";

export interface SummaryDescriptor {
  /** Relative to `ai.triggersPanel.summary.`. */
  key: string;
  params?: Record<string, unknown>;
}

/** 2024-01-01 was a Monday — anchor for ISO weekday (1=Mon … 7=Sun) names. */
function isoWeekdayName(isoWeekday: number, locale: string): string {
  const date = new Date(Date.UTC(2024, 0, isoWeekday));
  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    timeZone: "UTC",
  }).format(date);
}

/** "15 min" / "2 h" — universal abbreviations, no i18n needed. */
export function formatIntervalShort(seconds: number): string {
  if (seconds >= 3600 && seconds % 3600 === 0) {
    return `${seconds / 3600} h`;
  }
  return `${Math.round(seconds / 60)} min`;
}

export function describeScheduleSpec(
  spec: ScheduleSpec,
  locale: string,
): SummaryDescriptor {
  switch (spec.kind) {
    case "hourly":
      return {
        key: "schedule.hourly",
        params: { minute: String(spec.minute).padStart(2, "0") },
      };
    case "daily":
      return { key: "schedule.daily", params: { time: spec.time } };
    case "weekly": {
      const days = [...spec.weekdays]
        .sort((a, b) => a - b)
        .map((d) => isoWeekdayName(d, locale))
        .join(", ");
      return { key: "schedule.weekly", params: { days, time: spec.time } };
    }
    case "every_n_days":
      if (spec.n === 1) {
        return { key: "schedule.daily", params: { time: spec.time } };
      }
      return {
        key: "schedule.everyNDays",
        params: { n: spec.n, time: spec.time },
      };
  }
}

/**
 * Summary descriptor for any trigger. For poll triggers pass the resolved
 * saved-query title (caller owns the lookup + "unknown" fallback).
 */
export function describeDefinition(
  trigger: Pick<Trigger, "triggerType" | "definition">,
  opts: { locale: string; queryTitle?: string },
): SummaryDescriptor {
  switch (trigger.triggerType) {
    case "event": {
      const def = trigger.definition as EventDefinition;
      return { key: `event.${def.sourceType}` };
    }
    case "schedule": {
      const def = trigger.definition as ScheduleDefinition;
      return describeScheduleSpec(def.spec, opts.locale);
    }
    case "poll": {
      const def = trigger.definition as PollDefinition;
      return {
        key: "poll",
        params: {
          interval: formatIntervalShort(def.intervalSeconds),
          query: opts.queryTitle ?? def.queryId,
        },
      };
    }
  }
}
